import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Modal, Nav, Spinner, Tab } from "react-bootstrap";
import moment from "moment";
import { toast } from "sonner";
import useProfileStore from "../../store/patient/profileStore";
import { fetchDepositHistory } from "../../utils/patients/fetchDepositHistory";
import { payBillsFromWallet } from "../../utils/patients/payFromWallet";
import { useBills } from "../../context/paymentAndBillsContext";

const formatNaira = (value) =>
  Number(value || 0).toLocaleString("en-NG", {
    style: "currency",
    currency: "NGN",
  });

const titleCase = (value) =>
  (value || "")
    .toString()
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Wallet ledger for the patient: current balance, money-in (funding) and
// money-out (usage / transfers) history, plus a shortcut to clear pending
// bills straight from the wallet balance.
const WalletHistoryModal = ({ show, setShow }) => {
  const { profile } = useProfileStore();
  const { userBills, findPatientBillsAlt } = useBills();

  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [ledger, setLedger] = useState({
    balance: 0,
    totalFunded: 0,
    totalUsed: 0,
    funding: [],
    usage: [],
  });

  const load = useCallback(async () => {
    if (!profile?.id) return;
    setLoading(true);
    try {
      const res = await fetchDepositHistory(profile.id, "all");
      const data = res?.data || {};
      setLedger({
        balance: data.balance || 0,
        totalFunded: data.totalFunded || 0,
        totalUsed: data.totalUsed || 0,
        funding: Array.isArray(data.funding) ? data.funding : [],
        usage: Array.isArray(data.usage) ? data.usage : [],
      });
    } catch (err) {
      console.error("Error loading wallet history:", err);
      toast.error("Could not load wallet history.");
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const pendingBills = useMemo(
    () => (Array.isArray(userBills) ? userBills : []).filter((b) => b?.status !== 1),
    [userBills]
  );

  const pendingTotal = useMemo(
    () => pendingBills.reduce((t, b) => t + Math.abs(Number(b?.amount || 0)), 0),
    [pendingBills]
  );

  const handleClose = () => setShow(false);

  // Apply the wallet balance to as many pending bills as it can cover
  // (oldest first), mirroring the paypoint apply-deposit flow.
  const handlePayFromWallet = async () => {
    if (!pendingBills.length) {
      toast.info("You have no pending bills to pay.");
      return;
    }
    if (ledger.balance <= 0) {
      toast.error("Your wallet balance is ₦0. Fund your wallet first.");
      return;
    }

    const items = pendingBills.map((b) => ({
      id: b.id,
      amount: Math.abs(Number(b.amount || 0)),
    }));

    setPaying(true);
    try {
      const res = await payBillsFromWallet(items);
      if (res?.data?.success) {
        toast.success("Bills paid from your wallet.");
        await Promise.all([load(), findPatientBillsAlt?.()]);
      } else {
        toast.error(res?.data?.message || "Could not complete wallet payment.");
      }
    } catch (err) {
      console.error("Wallet payment error:", err);
      toast.error("Could not complete wallet payment.");
    } finally {
      setPaying(false);
    }
  };

  const renderRows = (rows, direction) => {
    if (loading) {
      return (
        <div className="text-center py-5 text-secondary">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      );
    }
    if (!rows.length) {
      return (
        <div className="text-center py-5 text-secondary">
          {direction === "in"
            ? "No funding yet. Fund your wallet to get started."
            : "No wallet usage yet."}
        </div>
      );
    }
    return (
      <ul className="wallet-ledger-list list-unstyled mb-0">
        {rows.map((row) => (
          <li
            key={row.id}
            className="d-flex justify-content-between align-items-start py-3 border-bottom"
          >
            <div className="pe-3">
              <div className="fw-medium">
                {row.note || titleCase(row.type) || "Wallet entry"}
              </div>
              <small className="text-secondary d-block">
                {moment(row.date).format("MMM D, YYYY • h:mm A")}
              </small>
              <small className="text-secondary">
                {row.method ? titleCase(row.method) : ""}
                {row.bill_source ? ` • ${titleCase(row.bill_source)}` : ""}
                {row.reference ? ` • Ref ${row.reference}` : ""}
              </small>
            </div>
            <div
              className={`fw-semibold text-nowrap ${
                direction === "in" ? "text-success" : "text-danger"
              }`}
            >
              {direction === "in" ? "+" : "−"}
              {formatNaira(row.amount)}
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Wallet</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="wallet-summary-panel p-3 mb-3 rounded">
          <small className="text-secondary d-block">Available balance</small>
          <h2 className="finance-value mb-2">{formatNaira(ledger.balance)}</h2>
          <div className="d-flex flex-wrap gap-3">
            <Badge bg="success-subtle" text="success" className="p-2">
              Funded: {formatNaira(ledger.totalFunded)}
            </Badge>
            <Badge bg="danger-subtle" text="danger" className="p-2">
              Used: {formatNaira(ledger.totalUsed)}
            </Badge>
          </div>

          {pendingBills.length > 0 && (
            <div className="mt-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <small className="text-secondary">
                {pendingBills.length} pending bill
                {pendingBills.length > 1 ? "s" : ""} • {formatNaira(pendingTotal)}
              </small>
              <Button
                size="sm"
                className="btn-deda"
                onClick={handlePayFromWallet}
                disabled={paying || ledger.balance <= 0}
              >
                {paying ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Paying…
                  </>
                ) : (
                  "Pay bills from wallet"
                )}
              </Button>
            </div>
          )}
        </div>

        <Tab.Container defaultActiveKey="funding">
          <Nav variant="pills" className="mb-3 gap-2">
            <Nav.Item>
              <Nav.Link eventKey="funding">Funding history</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="usage">Used history</Nav.Link>
            </Nav.Item>
          </Nav>
          <Tab.Content>
            <Tab.Pane eventKey="funding">{renderRows(ledger.funding, "in")}</Tab.Pane>
            <Tab.Pane eventKey="usage">{renderRows(ledger.usage, "out")}</Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="" className="btn-white" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WalletHistoryModal;
